import { Button, Typography, Icon, TextField, LabelField } from "@fanka/ui-admin";
import { PageLayout } from "@on-site/layouts/PageLayout";
import { useState } from "react";
import { QrCodeBarCode } from "@on-site/components/pages/default/ui/QrCodeBarCode";
import { useParams } from "@on-site/libs/router";
import { buildFullPath } from "@on-site/libs/fetch/utils";
import { useAppApiSWR } from "@on-site/libs/swr/useAppApiSWR";
import { usePatientNoCheckIn } from "@on-site/components/pages/default/hooks";
import { Controller } from "react-hook-form";
import { replaceZtoH } from "@on-site/utils/format";

export const DefaultPage = ({ onCheckIn }: { onCheckIn: (patientNo: string) => void }) => {
  const { clinicId } = useParams();

  const [open, setOpen] = useState<boolean>(false);
  const { inputRef, form, handleDebouncedPatientNo } = usePatientNoCheckIn();

  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const handleCheckIn = () => {
    onCheckIn(form.watch("patientNo"));
  };

  const clinicQuery = useAppApiSWR("/api/v2/public/clinics/{clinicId}", {
    params: { path: { clinicId: clinicId ?? "" } },
    enabled: !!clinicId,
  });

  const reservedURL = buildFullPath(
    process.env.RESERVE_BASE_URL || "https://reserve.fanka.jp",
    `/${clinicQuery.data?.clinicGroup.id}/${clinicQuery.data?.id}/newDirectVisit`,
  );

  const { isValid } = form.formState;

  return (
    <PageLayout>
      <div className="mx-auto flex h-full w-full max-w-[450px] flex-col items-center justify-center gap-y-16">
        <div className=" whitespace-pre-wrap text-center">
          <Typography.Heading type="h1">{`ご来院の方は、診察券コードで\nチェックインしてください`}</Typography.Heading>
        </div>
        <div className="flex w-full flex-col gap-y-4">
          <Controller
            control={form.control}
            name="patientNo"
            render={({ field, fieldState: { error, isDirty } }) => (
              <LabelField error={isDirty ? error?.message : ""}>
                <TextField
                  {...field}
                  onChange={(e) => {
                    const hankakuPatientNo = replaceZtoH(e.target.value);

                    handleDebouncedPatientNo(() => {
                      if (!!error?.message) return;
                      onCheckIn(hankakuPatientNo);
                    });

                    field.onChange(hankakuPatientNo);
                  }}
                  size="lg"
                  placeholder="診察券の読み取りまたは、患者番号を入力して下さい"
                  ref={inputRef}
                  isError={isDirty && !!error?.message}
                />
              </LabelField>
            )}
            key={`${reservedURL}-${open}`}
          />
          <Button size="lg" icon={<Icon.LoginIcon />} iconType="left" block onClick={handleCheckIn} disabled={!isValid}>
            チェックインする
          </Button>
        </div>
        <div className="w-full rounded border font-bold">
          <Button styleType="textLine" size="lg" block onClick={handleOpen}>
            はじめてご来院の方
          </Button>
        </div>
        <div className="text-ui-500 flex flex-col gap-y-4">
          <Typography.UiText size="lg">
            診察券コードは、メールアドレスやLINEへの予約通知、またはマイページにて確認できます。
          </Typography.UiText>
          <Typography.UiText size="lg">はじめてご来院の方は、受付でスタッフにお声がけ下さい。</Typography.UiText>
        </div>
      </div>
      <QrCodeBarCode open={open} onClose={handleClose} text={reservedURL} />
    </PageLayout>
  );
};
